/**
 * Node usage summary (inspector · inverse "where used") — the mirror of the Data
 * dock's forward badges. For the SELECTED component it answers "what data does
 * this node touch?": the queries + page vars it READS, and the page vars it
 * WRITES, straight from the `byNode` index of `findReferences`.
 *
 * Read-only chips (the forward jump lives on the dock badges). Renders nothing
 * when the node touches no page data, so it stays out of the way on plain nodes.
 */
import { ArrowDownToLine, ArrowUpFromLine } from "lucide-react";

import type { PageReferences } from "@/lib/pageReferences";

export function NodeUsageSummary({ refs, nodeId }: { refs?: PageReferences; nodeId: string }) {
  const entry = refs?.byNode[nodeId];
  if (!entry) return null;
  const { queriesRead, varsRead, varsWritten } = entry;
  if (queriesRead.length === 0 && varsRead.length === 0 && varsWritten.length === 0) return null;

  return (
    <div className="mb-3 rounded-md border border-border/60 bg-surface/40 p-2 text-[11px]">
      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
        Dati usati
      </div>
      {(queriesRead.length > 0 || varsRead.length > 0) && (
        <div className="mb-1 flex items-start gap-1.5">
          <ArrowDownToLine className="mt-0.5 h-3 w-3 shrink-0 text-info" strokeWidth={1.5} />
          <div className="flex flex-wrap gap-1">
            {queriesRead.map((q) => (
              <Chip key={`q:${q}`} text={`$queries.${q}`} tone="info" />
            ))}
            {varsRead.map((v) => (
              <Chip key={`vr:${v}`} text={`$page.${v}`} tone="info" />
            ))}
          </div>
        </div>
      )}
      {varsWritten.length > 0 && (
        <div className="flex items-start gap-1.5">
          <ArrowUpFromLine className="mt-0.5 h-3 w-3 shrink-0 text-warning" strokeWidth={1.5} />
          <div className="flex flex-wrap gap-1">
            {varsWritten.map((v) => (
              <Chip key={`vw:${v}`} text={`$page.${v}`} tone="warning" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Chip({ text, tone }: { text: string; tone: "info" | "warning" }) {
  return (
    <code
      className={`rounded px-1.5 py-0.5 font-mono text-[10px] ${
        tone === "warning" ? "bg-warning/10 text-warning" : "bg-info/10 text-info"
      }`}
    >
      {text}
    </code>
  );
}
